import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Grid,
  IconButton,
  SxProps,
  Switch,
  Theme,
  Tooltip,
  Typography,
} from '@mui/material'
import { FC, useEffect, useState } from 'react'

import { InfoOutlined, Launch, Save } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import dayjs from 'dayjs'
import { useMutation } from 'react-query'
import { useNavigate } from 'react-router-dom'
import { fullName } from '../../../../domain/Account/AccountDisplay'
import { getReadableSpecies } from '../../../../domain/Animal/AnimalDisplay'
import { CustomersClient } from '../../../../interactors/clients/CustomerClient'
import {
  AccountDto,
  AccountDtoPermission,
  AccountDtoRoles,
  AnimalDto,
  AnimalDtoSpecies,
  ChangeOfHostedFamilyHostedFamilyFollowUpDto,
  CustomerCageDto,
  ShiftHostedFamilyFollowUpDto,
  ShiftHostedFamilyFollowUpDtoType,
} from '../../../../interactors/gen/backendClient'
import { useAccountsStore } from '../../../../store/AccountsStore'
import { useEditAnimalMutation } from '../../../../store/useEditAnimalMutation'
import { useFetchOrRefreshMembersOnMount } from '../../../../store/useFetchOrRefreshMembersOnMount'
import { animalButtonShouldBeDisabled, isAnimalReleased } from '../../../../utils/animalButtonShouldBeDisabled'
import { toDateOnly } from '../../../../utils/date/DateOnly'
import { useFormExtended } from '../../../../utils/hooks/useFormExtended'
import { PATHS } from '../../../PATHS'
import { ControlledAutoCompleteWithTabs } from '../../../common/ControlledAutoCompleteWithTabs'
import { ControlledAutoCompleteWithCustomValue } from '../../../common/ControlledAutocompleteWithCustomValue'
import { MainHostedFamilyFollowUp } from '../HostedFammilyFollowUpsTab/HostedFamillyFollowUpsTab'
import { InfoBox } from '../../../common/InfoBox'

interface Props {
  animal: AnimalDto
  sx?: SxProps<Theme>
}

type FormParams = {
  memberInChargeAccountId: string | null
  animalLocation: string | null
  temporaryHostFamilyInChargeAccountId: string | null
  veterinaryChargeAccountId: string | null
}

export const AnimalResponsibilitiesCard: FC<Props> = ({ animal, sx }) => {
  const queryParams = new URLSearchParams(location.search)
  const accountsStore = useAccountsStore()
  const navigate = useNavigate()

  const [customer, setCustomer] = useState(accountsStore.connectedCustomer)

  const [isModalOpen, setIsModalOpen] = useState(false)
  const [modalMessage, setModalMessage] = useState('')
  const [isCustomerLoaded, setIsCustomerLoaded] = useState(false)
  const [isTemporaryHostFamilyVisible, setIsTemporaryHostFamilyVisible] = useState(
    Boolean(animal.responsibilities?.temporaryHostFamilyInChargeAccountId)
  )

  useFetchOrRefreshMembersOnMount()

  const fetchCustomerMutation = useMutation(async () => await CustomersClient.getCurrentCustomer(), {
    onSuccess: (data) => {
      setCustomer(data)
      setIsCustomerLoaded(true)
    },
  })
  useEffect(() => {
    fetchCustomerMutation.mutate()
  }, [])
  const connectedAccountId = accountsStore.connectedAccount?.id
  const connectedPermission = accountsStore.connectedAccount?.permission
  const isEditor = connectedPermission === AccountDtoPermission.Editor

  // A released animal is archived, but we still allow editing its référent and vétérinaire en charge.
  const isReleased = isAnimalReleased(animal)

  const options = accountsStore.members
  const optionsForMemberInCharge = options.filter((option) => {
    return option.roles.includes(AccountDtoRoles.Member)
  })

  const optionsForHostFamilyInCharge = options.filter((option) => {
    return option.roles.includes(AccountDtoRoles.HostFamily)
  })

  const optionsForVeterinaries = options.filter((account) => account.roles.includes(AccountDtoRoles.Veterinary))

  const initialVeterinaryOption = options.find(
    (member) => member.id === animal.responsibilities?.veterinaryChargeAccountId
  )

  if (connectedAccountId) {
    options.sort((x, y) => {
      return x.id === connectedAccountId ? -1 : y.id == connectedAccountId ? 1 : 0
    })
  }

  const initialMemberInChargeOption = accountsStore.withRemovedMembers.find(
    (member) => member.id === animal.responsibilities?.memberInChargeAccountId
  )
  const initialHostFamilyOption = accountsStore.withRemovedMembers.find(
    (member) => member.id === animal.responsibilities?.hostFamilyInChargeAccountId
  )
  const initialTemporaryHostFamilyOption = accountsStore.withRemovedMembers.find(
    (member) => member.id === animal.responsibilities?.temporaryHostFamilyInChargeAccountId
  )

  const getDefaultValues = () => ({
    memberInChargeAccountId: initialMemberInChargeOption?.id || null,
    animalLocation: initialHostFamilyOption?.id || animal.responsibilities?.cageNumber,
    temporaryHostFamilyInChargeAccountId: initialTemporaryHostFamilyOption?.id || null,
    veterinaryChargeAccountId: initialVeterinaryOption?.id || null,
  })

  const { control, handleSubmit, reset, setValue, watch, getValues } = useFormExtended(animal, getDefaultValues)

  useEffect(() => {
    reset(getDefaultValues())
  }, [initialMemberInChargeOption?.id])

  useEffect(() => {
    reset(getDefaultValues())
  }, [initialHostFamilyOption?.id])

  useEffect(() => {
    reset(getDefaultValues())
  }, [initialTemporaryHostFamilyOption?.id])

  useEffect(() => {
    setIsTemporaryHostFamilyVisible(Boolean(animal.responsibilities?.temporaryHostFamilyInChargeAccountId))
  }, [animal.id, animal.responsibilities?.temporaryHostFamilyInChargeAccountId])

  const getOptionLabel = (option: AccountDto): string => {
    let optionLabel = fullName(option)
    if (option.id === accountsStore.connectedAccount?.id) {
      optionLabel = `Moi (${optionLabel})`
    }

    if (option.isDeleted) {
      optionLabel = `Compte supprimé (${optionLabel})`
    }

    return optionLabel
  }

  const getHostFamilyOptions = (initialOption?: AccountDto, addOptionLabel = 'Ajouter une famille en charge') => [
    ...(!isEditor ? [{ value: 'addAccount', label: addOptionLabel }] : []),
    ...(accountsStore.connectedAccount && accountsStore.connectedAccount.roles.includes(AccountDtoRoles.HostFamily)
      ? [
          {
            value: accountsStore.connectedAccount?.id,
            label: getOptionLabel(accountsStore.connectedAccount),
          },
        ]
      : []),
    ...(initialOption && initialOption.id !== connectedAccountId
      ? [{ value: initialOption.id, label: getOptionLabel(initialOption) }]
      : []),
    ...optionsForHostFamilyInCharge
      .filter((option) => option.id !== connectedAccountId && option.id !== initialOption?.id)
      .sort((a, b) => (a.firstName + a.lastName).localeCompare(b.firstName + b.lastName))
      .map((option) => ({
        label: getOptionLabel(option),
        value: option.id,
      })),
  ]

  const editAnimalMutation = useEditAnimalMutation({})

  //Get readable reason for error message
  const getReason = (reason: string): string => {
    switch (reason) {
      case 'has-been-adopted':
        return 'adopté'
      case 'lost':
        return `enregistré comme perdu`
      case 'dead':
        return `enregistré comme décédé`
      case 'released':
        return `relâché`
      case 'transfer-to-another-association':
        return `transféré à une autre association`
      default:
        return ''
    }
  }

  // Helper function to create a new FollowUp
  const createNewFollowUp = (
    type: ShiftHostedFamilyFollowUpDtoType,
    destination: string,
    memberInChargeAccountId: string | undefined | null
  ): ShiftHostedFamilyFollowUpDto => {
    return {
      type,
      date: toDateOnly(dayjs()),
      destination,
      memberInChargeAccountId:
        accountsStore.connectedAccount?.id ||
        memberInChargeAccountId ||
        animal.responsibilities?.memberInChargeAccountId ||
        '',
      created: {
        by: accountsStore.connectedAccount?.id || '',
        at: dayjs().toISOString(),
      },
    }
  }

  // Helper function to create a new change-of-hosted-family FollowUp
  const createChangeOfHostedFamilyFollowUp = (
    beforeAccountId: string,
    afterAccountId: string
  ): ChangeOfHostedFamilyHostedFamilyFollowUpDto => ({
    type: 'change-of-hosted-family',
    date: toDateOnly(dayjs()),
    beforeAccountId,
    afterAccountId,
    created: {
      by: accountsStore.connectedAccount?.id || '',
      at: dayjs().toISOString(),
    },
  })

  //Check if data.animalLocation starts with "A-" if it does then setting hostFamilyInChargeAccountId if it does not then setting cageNumber
  const onSubmit = async (data: Partial<FormParams>, ignoreErrors?: boolean) => {
    let hostFamilyInChargeAccountId: string | undefined

    let cageNumber: string | undefined
    let cage: CustomerCageDto | undefined

    if (data.animalLocation === null || data.animalLocation === undefined) {
      hostFamilyInChargeAccountId = animal.responsibilities?.hostFamilyInChargeAccountId
      cageNumber = animal.responsibilities?.cageNumber
    } else if (data.animalLocation?.startsWith('A-')) {
      hostFamilyInChargeAccountId = data.animalLocation
    } else {
      cageNumber = data.animalLocation
      cage = customer?.cages?.find((cage) => cage.cageNumber === cageNumber)
    }

    if (!ignoreErrors) {
      if (
        cageNumber !== animal.responsibilities?.cageNumber &&
        cage &&
        cage.animals?.length &&
        cage.animals.length >= cage.capacity
      ) {
        setModalMessage(
          `La cage sélectionnée comporte déjà ${cage.animals.length} animaux alors qu'elle a une capacité de ${cage.capacity} animaux. Êtes-vous sûr de vouloir l'assigner à cet animal ?`
        )
        setIsModalOpen(true)
        return
      }
      if (
        cageNumber !== animal.responsibilities?.cageNumber &&
        cage &&
        !cage.suitableForSpecies.includes(animal.species)
      ) {
        setModalMessage(
          `La cage sélectionnée est prévu pour des ${cage.suitableForSpecies
            .map((specie) => getReadableSpecies(specie))
            .join(' ou ')}. Êtes-vous sûr de vouloir l'assigner à cet animal (${getReadableSpecies(animal.species)}) ?`
        )
        setIsModalOpen(true)
        return
      }
      if (animalButtonShouldBeDisabled(animal) && !isReleased) {
        setModalMessage(
          `L'animal a été ${getReason(
            animal.adoption.status === 'has-been-adopted'
              ? animal.adoption.status
              : animal.adoption.cannotBeAdoptedStatus || ''
          )} êtes-vous sur de vouloir changer cette responsabilité ?`
        )
        setIsModalOpen(true)
        return
      }
    }

    let newFollowUp: MainHostedFamilyFollowUp | undefined = undefined
    if (!animalButtonShouldBeDisabled(animal)) {
      if (cageNumber || hostFamilyInChargeAccountId) {
        const responsibilities = animal.responsibilities

        if (responsibilities?.cageNumber || responsibilities?.hostFamilyInChargeAccountId) {
          // Check if both cage and host family are different
          if (
            responsibilities?.cageNumber !== cageNumber &&
            responsibilities?.hostFamilyInChargeAccountId !== hostFamilyInChargeAccountId
          ) {
            const destination = cageNumber ? `Box N°${cageNumber}` : hostFamilyInChargeAccountId ?? ''
            newFollowUp = createNewFollowUp('shift', destination, data.memberInChargeAccountId)
          }
          // Check if only host family has changed
          else if (responsibilities?.hostFamilyInChargeAccountId !== hostFamilyInChargeAccountId) {
            newFollowUp = createChangeOfHostedFamilyFollowUp(
              responsibilities.hostFamilyInChargeAccountId ?? '',
              hostFamilyInChargeAccountId ?? ''
            )
          }
          // Check if only cage has changed (Add this block)
          else if (responsibilities?.cageNumber !== cageNumber) {
            const destination = `Box N°${cageNumber}`
            newFollowUp = createNewFollowUp('shift', destination, data.memberInChargeAccountId)
          }
        }
        // Handle initial placement
        else {
          const destination = cageNumber ? `Box N°${cageNumber}` : hostFamilyInChargeAccountId ?? ''
          newFollowUp = createNewFollowUp('initial-placement', destination, data.memberInChargeAccountId)
        }
      }
    }

    const newResponsibilities = {
      ...animal.responsibilities,
      cageNumber: cageNumber || undefined,
      hostFamilyInChargeAccountId: hostFamilyInChargeAccountId || undefined,
      temporaryHostFamilyInChargeAccountId:
        data.temporaryHostFamilyInChargeAccountId === undefined
          ? animal.responsibilities?.temporaryHostFamilyInChargeAccountId
          : data.temporaryHostFamilyInChargeAccountId || undefined,
      veterinaryChargeAccountId:
        data.veterinaryChargeAccountId || animal.responsibilities?.veterinaryChargeAccountId || undefined,
    }

    newResponsibilities.memberInChargeAccountId =
      data.memberInChargeAccountId || animal.responsibilities?.memberInChargeAccountId || undefined

    return await editAnimalMutation.mutate({
      ...animal,
      responsibilities: newResponsibilities,
      hostedFamilyFollowUps: {
        ...animal.hostedFamilyFollowUps,
        main: [...(animal.hostedFamilyFollowUps.main || []), newFollowUp].filter(
          (followUp) => followUp
        ) as MainHostedFamilyFollowUp[],
      },
    })
  }

  useEffect(() => {
    if (queryParams.get('tab') === 'general') {
      const newMemberInCharge = queryParams.get('memberInCharge')

      if (newMemberInCharge !== undefined && newMemberInCharge !== null) {
        onSubmit({ memberInChargeAccountId: newMemberInCharge || '' })
      }

      const newVeterinaryCharge = queryParams.get('veterinary')
      if (newVeterinaryCharge !== undefined && newVeterinaryCharge !== null) {
        onSubmit({ veterinaryChargeAccountId: newVeterinaryCharge || '' })
      }

      const newHostFamilyInCharge = queryParams.get('hostFamilyInCharge')

      if (newHostFamilyInCharge !== undefined && newHostFamilyInCharge !== null) {
        onSubmit({ animalLocation: newHostFamilyInCharge || '' })
      }

      const newCageNumber = queryParams.get('cageNumber')

      if (newCageNumber !== undefined && newCageNumber !== null) {
        setValue('animalLocation', newCageNumber)
        const cageSpecies = queryParams.get('species')
        if (cageSpecies && cageSpecies.split(',').includes(animal.species)) {
          onSubmit({ animalLocation: newCageNumber || '' })
        } else {
          setModalMessage(
            `La cage sélectionnée est prévu pour des ${
              cageSpecies &&
              cageSpecies
                .split(',')
                .map((specie) => getReadableSpecies(specie as AnimalDtoSpecies))
                .join(' ou ')
            }. Êtes-vous sûr de vouloir l'assigner à cet animal (${getReadableSpecies(animal.species)}) ?`
          )
          setIsModalOpen(true)
        }
      }
    }
  }, [])

  const memberInCharge = watch('memberInChargeAccountId')
  const memberProfilePath =
    memberInCharge && memberInCharge !== 'addAccount'
      ? PATHS.accountDetails.absolute.replace(':accountId', memberInCharge)
      : undefined
  useEffect(() => {
    if (memberInCharge === 'addAccount') {
      reset({ memberInChargeAccountId: null })
      navigate(
        `${PATHS.ajouterBenevole.absolute}?from=animaux/${animal.id}?tab=general&role=${AccountDtoRoles.Member}&card=animalResponsibilitiesCard`
      )
    }
  }, [memberInCharge])

  const animalLocation = watch('animalLocation')
  const hostFamilyProfilePath =
    typeof animalLocation === 'string' && animalLocation.startsWith('A-')
      ? PATHS.accountDetails.absolute.replace(':accountId', animalLocation)
      : undefined
  useEffect(() => {
    if (animalLocation === 'addAccount') {
      reset({ animalLocation: undefined })
      navigate(
        `${PATHS.ajouterBenevole.absolute}?from=animaux/${animal.id}?tab=general&role=${AccountDtoRoles.HostFamily}&card=animalResponsibilitiesCard`
      )
    } else if (animalLocation === 'addCage') {
      reset({ animalLocation: undefined })
      navigate(
        `${PATHS.association.absolute}?tab=cages&from=animaux/${animal.id}?tab=general&card=animalResponsibilitiesCard`
      )
    }
  }, [animalLocation])

  const temporaryHostFamilyInChargeAccountId = watch('temporaryHostFamilyInChargeAccountId')
  const temporaryHostFamilyProfilePath =
    temporaryHostFamilyInChargeAccountId && temporaryHostFamilyInChargeAccountId !== 'addAccount'
      ? PATHS.accountDetails.absolute.replace(':accountId', temporaryHostFamilyInChargeAccountId)
      : undefined
  useEffect(() => {
    if (temporaryHostFamilyInChargeAccountId === 'addAccount') {
      reset({ temporaryHostFamilyInChargeAccountId: null })
      navigate(
        `${PATHS.ajouterBenevole.absolute}?from=animaux/${animal.id}?tab=general&role=${AccountDtoRoles.HostFamily}&card=animalResponsibilitiesCard`
      )
    }
  }, [temporaryHostFamilyInChargeAccountId])

  const veterinaryChargeAccountId = watch('veterinaryChargeAccountId')
  const veterinaryProfilePath =
    veterinaryChargeAccountId && veterinaryChargeAccountId !== 'addAccount'
      ? PATHS.veterinaryDetails.absolute.replace(':accountId', veterinaryChargeAccountId)
      : undefined
  useEffect(() => {
    if (veterinaryChargeAccountId === 'addAccount') {
      reset({ veterinaryChargeAccountId: null })
      navigate(
        `${PATHS.ajouterBenevole.absolute}?from=animaux/${animal.id}?tab=general&role=${AccountDtoRoles.Veterinary}&card=animalResponsibilitiesCard`
      )
    }
  }, [veterinaryChargeAccountId])

  const [isButtonHidden, setIsButtonHidden] = useState(true)

  const normalizeResponsibilityValue = (value: string | null | undefined) => {
    if (value === null || value === undefined || value === '') return null
    return value
  }

  const initialMemberInChargeValue = normalizeResponsibilityValue(initialMemberInChargeOption?.id)
  const initialAnimalLocationValue = normalizeResponsibilityValue(
    initialHostFamilyOption?.id || animal.responsibilities?.cageNumber
  )
  const initialTemporaryHostFamilyInChargeValue = normalizeResponsibilityValue(initialTemporaryHostFamilyOption?.id)
  const initialVeterinaryChargeValue = normalizeResponsibilityValue(initialVeterinaryOption?.id)

  const hasPendingChanges =
    !animalButtonShouldBeDisabled(animal) &&
    (normalizeResponsibilityValue(memberInCharge) !== initialMemberInChargeValue ||
      normalizeResponsibilityValue(animalLocation) !== initialAnimalLocationValue ||
      normalizeResponsibilityValue(temporaryHostFamilyInChargeAccountId) !== initialTemporaryHostFamilyInChargeValue ||
      normalizeResponsibilityValue(veterinaryChargeAccountId) !== initialVeterinaryChargeValue)

  const profileShortcutButtonSx = {
    width: 40,
    height: 40,
    flexShrink: 0,
    color: 'secondary.main',
    border: '1px solid rgba(105, 141, 172, 0.3)',
    borderRadius: '14px',
    backgroundColor: 'white',
    '&:hover': {
      backgroundColor: 'rgba(105, 141, 172, 0.08)',
      borderColor: 'rgba(105, 141, 172, 0.45)',
    },
  }

  const renderProfileShortcut = (profilePath: string | undefined, tooltipTitle: string) => {
    if (!profilePath) return null

    return (
      <Tooltip title={tooltipTitle}>
        <IconButton
          type="button"
          color="secondary"
          onMouseDown={(event) => event.preventDefault()}
          onClick={() => navigate(profilePath)}
          sx={profileShortcutButtonSx}
        >
          <Launch fontSize="small" />
        </IconButton>
      </Tooltip>
    )
  }

  const renderInlineInfoShortcut = (tooltipTitle: string) => (
    <Tooltip title={tooltipTitle} arrow enterTouchDelay={0} leaveTouchDelay={3000}>
      <IconButton
        type="button"
        color="secondary"
        onMouseDown={(event) => event.preventDefault()}
        sx={{
          width: 18,
          height: 18,
          p: 0,
          color: 'secondary.main',
          '& svg': {
            fontSize: 14,
          },
        }}
      >
        <InfoOutlined />
      </IconButton>
    </Tooltip>
  )

  const temporaryHostFamilyLabel = (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4 }}>
      <Box component="span">FA temporaire</Box>
      {renderInlineInfoShortcut("Infos indicatives, sans impact sur les droits d'édition.")}
    </Box>
  )

  const temporaryHostFamilyToggleLabel = (
    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.4 }}>
      <Box component="span">FA temporaire</Box>
      {renderInlineInfoShortcut("Infos indicatives, sans impact sur les droits d'édition.")}
    </Box>
  )

  useEffect(() => {
    if (
      ([AccountDtoPermission.Read, AccountDtoPermission.Silent] as Array<AccountDtoPermission>).includes(
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        connectedPermission!
      )
    ) {
      setIsButtonHidden(true)
      return
    }

    if (connectedPermission === AccountDtoPermission.Administrator) {
      console.log('admin')
      setIsButtonHidden(false)
      return
    }

    if (connectedPermission?.includes(AccountDtoPermission.Editor)) {
      const memberChangedForbidden = !initialMemberInChargeOption?.id
        ? false
        : initialMemberInChargeOption?.id !== connectedAccountId && initialMemberInChargeOption?.id !== memberInCharge

      const hostFamilyChangedForbidden = !initialHostFamilyOption?.id
        ? false
        : initialHostFamilyOption?.id !== connectedAccountId &&
          initialHostFamilyOption?.id !== animalLocation &&
          connectedAccountId !== memberInCharge

      if (memberChangedForbidden || hostFamilyChangedForbidden) {
        setIsButtonHidden(true)
        return
      }
    }

    setIsButtonHidden(false)
  })

  if (!isCustomerLoaded) {
    return (
      <Card sx={sx}>
        <CardContent>
          <CircularProgress />
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      <Card sx={sx}>
        <form onSubmit={handleSubmit((data) => onSubmit(data))}>
          <CardHeader
            title={
              <Box
                sx={{
                  display: 'flex',
                  alignItems: { xs: 'flex-start', sm: 'center' },
                  flexWrap: 'wrap',
                  justifyContent: 'space-between',
                  width: '100%',
                  columnGap: 1.5,
                  rowGap: 0.75,
                }}
              >
                <Typography component="span" variant="h5" sx={{ fontWeight: 700, color: '#203864' }}>
                  👷🏻‍♀️ Responsabilités
                </Typography>
                <FormControlLabel
                  label={temporaryHostFamilyToggleLabel}
                  labelPlacement="start"
                  control={
                    <Switch
                      size="small"
                      checked={isTemporaryHostFamilyVisible}
                      onChange={(_, checked) => setIsTemporaryHostFamilyVisible(checked)}
                    />
                  }
                  sx={{
                    m: 0,
                    ml: { xs: 0, sm: 'auto' },
                    gap: 0.5,
                    '& .MuiFormControlLabel-label': {
                      fontSize: 13,
                      fontWeight: 400,
                      color: 'text.secondary',
                    },
                    '& .MuiSwitch-root': {
                      ml: 0.25,
                    },
                  }}
                />
              </Box>
            }
          />
          <CardContent sx={{ pt: '0 !important' }}>
            {isEditor &&
              !!initialHostFamilyOption?.id &&
              initialHostFamilyOption?.id !== connectedAccountId &&
              connectedAccountId !== memberInCharge && (
                <InfoBox
                  messageType="info"
                  content="Vous ne pouvez éditer que les fiches des animaux auxquels vous êtes assigné(e) (Référent, FA et/ou vétérinaire). Si nécessaire, contactez un administrateur pour vous assigner cet animal."
                  sx={{ mb: 2 }}
                />
              )}
            {animalButtonShouldBeDisabled(animal) && (
              <Typography variant="body1" sx={{ mb: 1 }}>
                {isReleased
                  ? `${animal.name} est relâché. Vous pouvez uniquement modifier le référent et le vétérinaire en charge.`
                  : `${animal.name} est ${getReason(
                      animal.adoption.status === 'has-been-adopted'
                        ? animal.adoption.status
                        : animal.adoption.cannotBeAdoptedStatus || ''
                    )}, il est donc archivé. Vous ne pouvez pas modifier.`}
              </Typography>
            )}
            <Grid
              container
              spacing={1.5}
              sx={{
                alignItems: { xs: 'stretch', md: 'flex-end' },
                flexWrap: { xs: 'wrap', md: 'nowrap' },
              }}
            >
              <Grid item xs={12} md sx={{ minWidth: 0 }}>
                <Grid container spacing={1.25} sx={{ alignItems: { xs: 'stretch', md: 'flex-end' } }}>
                  <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, width: '100%', height: '100%' }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <ControlledAutoCompleteWithCustomValue
                          control={control}
                          fieldName={'memberInChargeAccountId'}
                          defaultValue=""
                          size="small"
                          options={[
                            ...(!isEditor ? [{ value: 'addAccount', label: 'Ajouter un bénévole' }] : []),
                            ...(accountsStore.connectedAccount &&
                            accountsStore.connectedAccount.roles.includes(AccountDtoRoles.Member)
                              ? [
                                  {
                                    value: accountsStore.connectedAccount?.id,
                                    label: getOptionLabel(accountsStore.connectedAccount),
                                  },
                                ]
                              : []),
                            ...(initialMemberInChargeOption && initialMemberInChargeOption.id !== connectedAccountId
                              ? [
                                  {
                                    value: initialMemberInChargeOption.id,
                                    label: getOptionLabel(initialMemberInChargeOption),
                                  },
                                ]
                              : []),
                            ...optionsForMemberInCharge
                              .filter(
                                (option) =>
                                  option.id !== connectedAccountId && option.id !== initialMemberInChargeOption?.id
                              )
                              .sort((a, b) => (a.firstName + a.lastName).localeCompare(b.firstName + b.lastName))
                              .map((option) => ({
                                label: getOptionLabel(option),
                                value: option.id,
                              })),
                          ]}
                          label="Référent en charge"
                          removeCustomValue={isEditor}
                          error={undefined}
                          requiredRule={undefined}
                          highlight
                          disabled={
                            (
                              [AccountDtoPermission.Read, AccountDtoPermission.Silent] as Array<AccountDtoPermission>
                            ).includes(connectedPermission!) ||
                            (isEditor &&
                              !!initialMemberInChargeOption?.id &&
                              initialMemberInChargeOption?.id !== connectedAccountId)
                          }
                        />
                      </Box>
                      {renderProfileShortcut(memberProfilePath, 'Voir le profil du référent')}
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, width: '100%', height: '100%' }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <ControlledAutoCompleteWithTabs
                          control={control}
                          fieldName={'animalLocation'}
                          defaultValue={initialHostFamilyOption?.id || animal.responsibilities?.cageNumber || ''}
                          size="small"
                          primaryTabLabel="Famille en charge"
                          primaryOptions={getHostFamilyOptions(initialHostFamilyOption)}
                          secondaryTabLabel="Boxes"
                          secondaryOptions={
                            accountsStore.isPremium()
                              ? [
                                  ...(!isEditor ? [{ value: 'addCage', label: 'Ajouter une cage' }] : []),
                                  ...(customer?.cages || [])
                                    .sort((a, b) => Number(a.cageNumber) - Number(b.cageNumber))
                                    .map((option) => ({
                                      label: `Box N°${option.cageNumber}`,
                                      value: option.cageNumber,
                                    })),
                                ]
                              : []
                          }
                          label="Lieu de résidence"
                          noOptionsTexts={
                            accountsStore.isPremium()
                              ? ['Aucune famille en charge', 'Aucune cage']
                              : [
                                  'Aucune famille en charge',
                                  "Vous devez souscrire à l'offre utile pour utiliser des cages",
                                ]
                          }
                          error={undefined}
                          requiredRule={undefined}
                          highlight
                          removeCustomValue={isEditor}
                          disabled={
                            (
                              [AccountDtoPermission.Read, AccountDtoPermission.Silent] as Array<AccountDtoPermission>
                            ).includes(connectedPermission!) ||
                            (isEditor &&
                              !!initialHostFamilyOption?.id &&
                              initialHostFamilyOption?.id !== connectedAccountId &&
                              connectedAccountId !== memberInCharge) ||
                            (animalButtonShouldBeDisabled(animal) && !isReleased)
                          }
                        />
                      </Box>
                      {renderProfileShortcut(hostFamilyProfilePath, "Voir le profil de la famille d'accueil")}
                    </Box>
                  </Grid>
                  <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, width: '100%', height: '100%' }}>
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <ControlledAutoCompleteWithCustomValue
                          control={control}
                          fieldName={'veterinaryChargeAccountId'}
                          defaultValue=""
                          size="small"
                          options={[
                            { value: 'addAccount', label: 'Ajouter un vétérinaire' },
                            ...(accountsStore.connectedAccount &&
                            accountsStore.connectedAccount?.roles.includes(AccountDtoRoles.Veterinary)
                              ? [
                                  {
                                    value: accountsStore.connectedAccount?.id,
                                    label: getOptionLabel(accountsStore.connectedAccount),
                                  },
                                ]
                              : []),
                            ...optionsForVeterinaries
                              .filter((option) => option.id !== accountsStore.connectedAccount?.id)
                              .sort((a, b) => (a.firstName + a.lastName).localeCompare(b.firstName + b.lastName))
                              .map((option) => ({
                                label: getOptionLabel(option),
                                value: option.id,
                              })),
                          ]}
                          label="Vétérinaire en charge"
                          error={undefined}
                          requiredRule={undefined}
                          highlight
                          disabled={
                            (
                              [AccountDtoPermission.Read, AccountDtoPermission.Silent] as Array<AccountDtoPermission>
                            ).includes(connectedPermission!) ||
                            (isEditor &&
                              !!initialHostFamilyOption?.id &&
                              initialHostFamilyOption?.id !== connectedAccountId &&
                              connectedAccountId !== memberInCharge) ||
                            (animalButtonShouldBeDisabled(animal) && !isReleased)
                          }
                        />
                      </Box>
                      {renderProfileShortcut(veterinaryProfilePath, 'Voir le profil du vétérinaire')}
                    </Box>
                  </Grid>
                </Grid>
              </Grid>
              <Grid item xs={12} md="auto">
                {/* We don't use AnimalCardSaveButton as permissions are specific for this one */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: { xs: 'stretch', md: 'flex-end' },
                    width: '100%',
                    alignItems: 'flex-end',
                    height: '100%',
                  }}
                >
                  {!isButtonHidden && (
                    <LoadingButton
                      color="primary"
                      variant={hasPendingChanges ? 'contained' : 'outlined'}
                      type="submit"
                      loading={editAnimalMutation.isLoading}
                      disabled={!hasPendingChanges}
                      sx={{
                        minHeight: 42,
                        px: 2.75,
                        borderRadius: '999px',
                        textTransform: 'none',
                        fontSize: 13,
                        fontWeight: 700,
                        borderColor: 'rgba(122, 129, 151, 0.24)',
                        backgroundColor: hasPendingChanges ? undefined : 'rgba(255, 255, 255, 0.72)',
                        boxShadow: 'none',
                        width: { xs: '100%', md: 'auto' },
                        whiteSpace: 'nowrap',
                        '&:hover': {
                          borderColor: 'rgba(122, 129, 151, 0.35)',
                          boxShadow: 'none',
                        },
                      }}
                    >
                      <Save sx={{ marginRight: 1 }} />
                      {hasPendingChanges ? 'Mettre à jour' : 'Aucun changement'}
                    </LoadingButton>
                  )}
                </Box>
              </Grid>
            </Grid>
            {isTemporaryHostFamilyVisible && (
              <Grid container spacing={1.5} sx={{ mt: 0.25 }}>
                <Grid item xs={12} md={4} sx={{ display: 'flex' }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1, width: '100%', height: '100%' }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <ControlledAutoCompleteWithCustomValue
                        control={control}
                        fieldName={'temporaryHostFamilyInChargeAccountId'}
                        defaultValue={initialTemporaryHostFamilyOption?.id || ''}
                        size="small"
                        options={getHostFamilyOptions(
                          initialTemporaryHostFamilyOption,
                          "Ajouter une famille d'accueil"
                        )}
                        label={temporaryHostFamilyLabel}
                        removeCustomValue={isEditor}
                        error={undefined}
                        requiredRule={undefined}
                        highlight
                        disabled={
                          (
                            [AccountDtoPermission.Read, AccountDtoPermission.Silent] as Array<AccountDtoPermission>
                          ).includes(connectedPermission!) ||
                          (isEditor &&
                            !!initialHostFamilyOption?.id &&
                            initialHostFamilyOption?.id !== connectedAccountId &&
                            connectedAccountId !== memberInCharge) ||
                          (animalButtonShouldBeDisabled(animal) && !isReleased)
                        }
                      />
                    </Box>
                    {renderProfileShortcut(temporaryHostFamilyProfilePath, 'Voir le profil de la FA temporaire')}
                  </Box>
                </Grid>
              </Grid>
            )}
          </CardContent>
        </form>
      </Card>

      <Dialog
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        PaperProps={{
          style: { borderRadius: 15, padding: 15 },
        }}
      >
        <DialogContent>
          <h2>Attention !</h2>
          <p>{modalMessage}</p>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setIsModalOpen(false)
              reset(getDefaultValues())
            }}
            color="error"
          >
            Annuler
          </Button>
          <Button
            onClick={() => {
              setIsModalOpen(false)
              onSubmit(getValues(), true)
            }}
            color="warning"
          >
            Enregistrer quand même
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}
